// Observability probe for TOF-612 — the intermittent "duplicate/stacked search
// boxes" defect in admin list views. The bug is timing/navigation dependent and
// has resisted on-demand reproduction, so instead of guessing at a fix we detect
// the defect the moment it happens in production and capture the exact DOM +
// navigation state that produced it.
//
// Each column's search/filter box is teleported into a header cell marked with
// `${id}_header_${column}`. DataTables' scrolling layout keeps the *visible*
// header in `.dt-scroll-head` and a height-collapsed sizing clone in
// `.dt-scroll-body`, so a healthy table has exactly one box in the visible
// header per searchable column. More than one visible box for any column is the
// user-facing defect.

export interface DuplicateColumnCount {
  visible: number; // boxes in the visible header (what the user sees)
  total: number; // boxes anywhere in this table (includes the hidden sizing clone)
}

export interface DuplicateReport {
  datatableId: string;
  affectedColumns: string[];
  columnCounts: Record<string, DuplicateColumnCount>;
  tableInstances: number; // tables sharing this id — >1 means a stale instance leaked across navigation
  scrollHeads: number; // >1 hints at a stale/duplicated scroll structure
  filtersOpen: boolean;
  totalVisibleBoxes: number;
}

const BOX = "input.column-search-field";

// Turbo's visit action for the navigation that produced the current page —
// "restore" (back/forward, cache hit) is the prime suspect for the leak.
let lastTurboAction = "initial";
if (typeof document !== "undefined") {
  document.addEventListener("turbo:visit", (e: Event) => {
    const detail = (e as CustomEvent).detail;
    lastTurboAction = detail?.action ?? "unknown";
  });
}

export const lastNavigationType = (): string => lastTurboAction;

// Returns a report only when at least one column shows more than one box in the
// visible header; otherwise null (healthy). Pure and DOM-driven so it can be
// unit-tested against constructed fixtures.
export const inspectDuplicateFilters = (
  container: Element | null,
  id: string,
  columns: Array<string | null>
): DuplicateReport | null => {
  if (!container) return null;

  const hasScrollHead = !!container.querySelector(".dt-scroll-head");
  const columnCounts: Record<string, DuplicateColumnCount> = {};
  const affectedColumns: string[] = [];
  let totalVisibleBoxes = 0;

  columns.forEach((col) => {
    if (col == null) return;
    const base = `${id}_header_${col}`;
    const visibleScope = hasScrollHead
      ? `.dt-scroll-head .${base}`
      : `.${base}`;
    const visible = container.querySelectorAll(`${visibleScope} ${BOX}`).length;
    const total = container.querySelectorAll(`.${base} ${BOX}`).length;
    columnCounts[col] = { visible, total };
    totalVisibleBoxes += visible;
    if (visible > 1) affectedColumns.push(col);
  });

  if (affectedColumns.length === 0) return null;

  return {
    datatableId: id,
    affectedColumns,
    columnCounts,
    tableInstances: id ? document.querySelectorAll(`[id="${id}"]`).length : 1,
    scrollHeads: container.querySelectorAll(".dt-scroll-head").length,
    filtersOpen: !!container.querySelector(".col-filter.active"),
    totalVisibleBoxes
  };
};
